// CreditBlocker — the full-viewport stop when the workspace has spent every
// credit (tickets/fable.md, Nika 2026-08-13: "beautiful workspace-blocker
// when workspace runs out of credits").
//
// Condition (all through functions/creditGuard.js's derivation — the data
// is sdk.getCreditBalance, never a literal): guard loaded, no error, status
// 'exhausted', a workspace resolved, and the route is NOT one of the
// recovery/identity surfaces (/admin/usage to top up, auth + profile so
// nobody is locked out of their own account chrome). A metering ERROR never
// blocks — a billing outage must not take the product down with it.
//
// Shape: geex poster grammar — one giant thin Code-family numeral (the
// Reminders "Alarm archetype": G/'200' is the thin register's legal floor),
// a mixed-weight identity line, whisper captions, ONE primary action. Calm
// adaptive `dialog` surface, not an alarm bar: the state is a hard fact,
// the surface stays composed. Admins get the top-up CTA (→ /admin/usage,
// the real usage page — tickets/opus.md "credits-exhausted UX": `/credits`
// was never a registered route, so the CTA silently bootShell-replaceState'd
// back to `/` — pages/index.js only ever registered `/admin/usage`); members
// get told who can fix it instead of a dead button (house rule: no fake
// affordances).
//
// Mounted at the shell root beside NoOrgOverlay/PersonaPill — same
// `display:'contents'` wrapper + fixed descendant idiom (a shell-root
// sibling with a real box becomes a static child of the shell's `1fr` grid
// row and pushes routed content into a collapsed implicit row — the
// DemoClaimBanner lesson PersonaPill documents).
//
// zIndex 10300: above the dropdown layers (DropdownRoot 10010, context menu
// 10100) — a leftover open menu must not float over the gate — and below
// ModalRoot (10500) so account/auth modals stay reachable above it.
//
// Blurred-overlay shape (Nika 2026-08-13: "make workspace blocker page on 0
// credit nice blurred overlay") — the DESIGN_LANGUAGE.md §7.5 Modal/Sheet
// chassis, applied here: a full-viewport `scrim` + `backdropFilter: blur`
// backdrop (byte-identical treatment to `Modal.js`/`AiActionConfirmCard.js` —
// the canonical veil, never a theme-invariant opaque fill) with the actual
// copy living in a floating `Card` (`background: 'document'`, `round:
// 'radiusSheet'`, `shadow: 'overlay'` — §2.3's two-shadow budget, never a
// bespoke boxShadow string). The workspace underneath stays visible-but-
// blurred rather than fully occluded — this is the §9 depleted state
// designed as a floating moment, not a second full page. `role: 'dialog'` +
// `ariaModal` move onto the Card itself (the actual dialog surface), not the
// backdrop, matching the Modal/ModalWindow split.

// DISMISSIBILITY + SCOPE (CREDIT-BLOCK-1 defect #3, tickets/opus.md). This
// gate shipped with NO close affordance, no Escape, no backdrop dismissal —
// its only interactive descendant was "Top up credits" — while covering
// 100% of the viewport on every route. Its own copy promised that "AI,
// builds and metered actions are paused", but the implementation also blocked
// pure reads that cost nothing (viewing a channel, a calendar, a teammate),
// and three independent lanes reproduced it as an inescapable trap.
//
// It is now a NOTICE, not a trap: Escape, the ✕, and a backdrop click each
// dismiss it for the session (`_creditBlockerDismissed` — workspace-scoped
// in state.js, so a switch re-announces for the new tenant; deliberately NOT
// a pref cookie — a `smbls_pref_*` cookie's one-year, cross-app, cross-tab
// scope would outlive the tab and suppress the notice for a genuinely new
// session too). CREDITS-GATE-UI-BREAK-1: that in-memory state alone meant
// any remount — including ordinary dev-server HMR/live-reload churn, not
// just a hard reload — wiped the dismissal and re-armed the gate on every
// single page mount, measured live re-showing on every navigation all
// session. `scope.close` below now ALSO mirrors the dismissal into
// per-workspace sessionStorage (`writeCreditBlockerDismissed`,
// functions/creditGuard.js), and `Overlay.hide` seeds it back on the next
// mount (`readCreditBlockerDismissed`) — sessionStorage dies with the tab,
// so a genuinely new tab still sees the gate once; only a remount WITHIN
// the same tab is now silent.
//
// Metered actions stay gated where they are actually spent — the server throws
// INSUFFICIENT_CREDITS / SPEND_CAP_EXCEEDED / ALLOCATION_EXCEEDED at the
// point of use and notifyCreditsExhaustedFromMessage surfaces it — so
// nothing is un-gated by letting the user read past this card. Once
// dismissed, CreditBanner carries the state persistently, so the two still
// never co-render.
//
// It also no longer fires on an UNKNOWN balance: `creditGuardState` returns
// `'unknown'` (never `'exhausted'`) when the balance could not be read, and
// only `'exhausted'` reaches the gate below. Nothing may be blocked on a
// value nobody positively confirmed.
//
// THE NOTICE IS A MODAL LAYER (persona audit round 1, elena, a direct sign-up):
// the first screen of a new account was this notice drawn over the welcome
// steps, and its ✕, Escape and the veil did nothing while Tab walked to "Skip
// setup" BEHIND it. Nothing in the handlers was broken — the welcome surface
// (OnboardingWizard) is a modal layer too: its `inertOutside` puts `inert` on
// every element outside it, and this notice, a sibling at the shell root, is
// outside it. An inert element takes no pointer, no focus, no keyboard, so the
// notice was painted above (z-index) and dead beneath (inert); in a workspace
// with no welcome open the same ✕ closed it. The framework's contract is
// "a dialog that opens over a layer must itself be a layer", and the
// `Overlay` below now is one: `inertOutside` on the same predicate that
// paints it (`creditBlockerShown`), so while it is up the page behind — the
// welcome included — is the inert one, focus moves in, Tab stays in (the
// Card's own trap, as AppDialog does), and closing hands the page, and the
// focus, back exactly as they were. The other order — the welcome opening AFTER
// the notice — is the welcome's to yield: components/OnboardingWizard.js
// `inertOutside` asks this same `creditBlockerShown`, so the notice stays the
// newest layer and the welcome waits under it. Locked by
// CreditBlocker.render.test.js (both orders).
export const CreditBlocker = {
  tag: 'div',
  display: 'contents',
  subscribeTo: [
    '_creditGuard',
    'currentPath',
    'workspace',
    'signedIn',
    'roles',
    '_creditBlockerDismissed'
  ],

  // Escape-to-dismiss now routes through the SAME chassis `shared/Modal.js`
  // uses (2026-08-15 — `packages/shared/**` write access granted, see
  // Modal.js's own header comment for the full FA409/FA503 fix): no
  // document-level listener, no `window`/context-flag once-guard at all —
  // just a declarative `onKeydown` on `Overlay` (the gate's dialog root,
  // below), fed by moving focus onto `Card` when the gate opens
  // (`scope.manageFocus` below; `Card` already carries
  // `role="dialog"`/`ariaModal`, and now `tabindex: '-1'` so it's
  // programmatically focusable). Escape bubbles from whichever descendant
  // holds focus up to `Overlay`'s `onKeydown` exactly like `AppDialog.js`
  // and the fixed `Modal.js` already prove live. This directly replaces the
  // FA503 divergence this file used to carry (a document-level keydown
  // listener bound via a context-scoped flag, justified at the time by "the
  // gate is not a focusable element" — it is, now, via `Card`'s own
  // `tabindex`).
  scope: {
    manageFocus(el, isOpen) {
      if (typeof document === 'undefined') return
      if (isOpen === el._focusWasOpen) return
      el._focusWasOpen = isOpen
      if (isOpen) {
        el._returnFocusTo = document.activeElement
        const raf =
          typeof requestAnimationFrame === 'function' ? requestAnimationFrame : (fn) => fn()
        raf(() => {
          const card = el.lookdown && el.lookdown('Card')
          if (card && card.node && typeof card.node.focus === 'function') card.node.focus()
        })
      } else {
        if (el._returnFocusTo && el._returnFocusTo.focus) el._returnFocusTo.focus()
        el._returnFocusTo = null
      }
    },
    // `close(el, s)` — el first, matching shared/Modal.js's own `close(el,
    // s)` (the chassis this Escape/backdrop handling mirrors) and this same
    // scope's `manageFocus(el, isOpen)` above. `scope` methods are plain
    // properties called off `el.scope.X(...)` — `this` inside resolves to
    // `el.scope`, not the element, so `el` must travel in explicitly to
    // reach `el.call` below (same reason `manageFocus` already takes it).
    close(el, s) {
      const root = s?.root
      if (!root || !root.update) return
      root.update({ _creditBlockerDismissed: true })
      // Mirror the dismissal into per-workspace sessionStorage so it
      // survives a remount within this tab (CREDITS-GATE-UI-BREAK-1 — see
      // functions/creditGuard.js's `writeCreditBlockerDismissed` header for
      // the full rationale). Best-effort: a storage failure must never
      // block the in-memory dismiss above.
      const wsId = root.workspace?.id || root.workspace?._id || null
      if (wsId && el && typeof el.call === 'function') {
        el.call('writeCreditBlockerDismissed', wsId, true)
      }
    }
  },

  // ONE threshold-notification driver for the whole guard family (the
  // banner deliberately has none — two drivers would race the pref write).
  // notifyCreditThreshold derives status itself, early-returns for
  // non-admins and already-announced levels, so firing on every
  // `_creditGuard` change is idempotent.
  onUpdate: (el, s) => {
    if (s.root && s.root._creditGuard) el.call('notifyCreditThreshold', el, s.root)
  },

  Overlay: {
    hide: (el, s) => {
      // The ONE predicate for "the notice is up" (functions/creditGuard.js
      // creditBlockerShown) — seeding a sessionStorage dismissal into ROOT
      // STATE on this paint path, so CreditBanner's CreditWarningPill (which
      // reads `root._creditBlockerDismissed` directly) takes over as the
      // persistent indicator exactly like an in-session dismissal — the two
      // still never co-render.
      const blocked = !!el.call('creditBlockerShown', el, s.root, true)
      // Piggy-back on this reactive read (re-evaluated every render, same
      // technique `shared/Modal.js`'s `isOpen` and `AppDialog.js`'s `hide`
      // use) to move focus onto `Card` on open / restore it on close — see
      // the root `scope.manageFocus` comment above.
      el.scope.manageFocus(el, blocked)
      return !blocked
    },
    // THE LAYER. While the notice is up, everything OUTSIDE it is inert — the
    // welcome surface, the shell, a toast — exactly as outside a modal
    // <dialog>, and the layer moves focus in and gives it back (the framework's
    // `inertOutside`, smbls element/src/inertOutside.js). Gated on the SAME
    // predicate as `hide` above, never on visibility: a layer hidden with
    // `hide:` stays modal, so a different predicate would leave the page dead
    // under an invisible notice. No state write here — a prop that decides
    // modality is a pure read.
    inertOutside: (el, s) => !!el.call('creditBlockerShown', el, s.root, false),
    inertOutsideDeps: [
      'signedIn',
      'currentPath',
      'workspace',
      '_creditGuard',
      '_creditBlockerDismissed'
    ],
    // Escape bubbles here from `Card` (focused by `manageFocus` above) and
    // STOPS here, as the AppDialog chassis does: the one Escape that closes the
    // notice must not also close whatever listens above it.
    onKeydown: (e, el, s) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      el.scope.close(el, s)
    },
    // Backdrop click dismisses. `e.target !== el.node` means the click
    // landed on the veil itself, not on anything inside the Card — the same
    // discrimination shared/Modal.js's maybeDismiss makes.
    onClick: (e, el, s) => {
      if (e.target !== el.node) return
      el.scope.close(el, s)
    },
    position: 'fixed',
    inset: '0',
    zIndex: '10300',
    // The veil, not the surface — byte-identical grammar to Modal.js /
    // AiActionConfirmCard.js: `scrim` is the adaptive theme-mirrored dim
    // (white-tinted on light, ink on dark — DESIGN_LANGUAGE §4.1), and
    // `blur(4px)` matches the canonical Modal chassis exactly (§7.5) so the
    // workspace behind the gate reads as blurred-but-present, not a second
    // opaque page.
    background: 'scrim',
    backdropFilter: 'blur(4px)',
    align: 'center center',
    padding: 'C',
    overflow: 'auto',
    overscrollBehavior: 'contain',
    '@mobileL': { padding: 'B' },

    // The floating card — the ONLY opaque surface in this gate. `document`
    // (opaque, adaptive both themes, §2.2) + `radiusSheet` + the `overlay`
    // elevation shadow (§2.3's two-shadow budget — never a bespoke
    // boxShadow string). `role`/`ariaModal` live here (the real dialog
    // surface), not on the backdrop, matching Modal/ModalWindow's split.
    Card: {
      position: 'relative',
      background: 'document',
      round: 'radiusSheet',
      shadow: 'overlay',
      flow: 'y',
      align: 'center center',
      gap: 'A',
      padding: 'C',
      maxWidth: 'I',
      width: '100%',
      boxSizing: 'border-box',
      textAlign: 'center',
      role: 'dialog',
      ariaModal: 'true',
      ariaLabel: (el) => el.call('polyglot', 'workspace.creditBlocker.aria'),
      // Programmatically focusable, not tab-reachable — `scope.manageFocus`
      // moves focus here on open so Escape (declared on `Overlay`, above)
      // has a focused descendant to bubble from. Same idiom as brand's
      // `ModalWindow` (`tabindex: '-1'`).
      tabindex: '-1',
      // Tab / Shift+Tab cycle between the ✕ and the action and never leave —
      // the same scoped trap every AppDialog panel and the welcome surface
      // run (appDialogTrapTab), so keyboard focus stays in the notice while
      // it is up (the layer above keeps the page behind out of reach).
      onKeydown: (e, el) => el.call('appDialogTrapTab', e),
      '@mobileL': { padding: 'B' },

      // The close affordance this gate shipped without. Absolute inside the
      // Card's own relative box so it never enters the centred content flow
      // (zero shift). THE house close X (brand CloseButton — F2, one X for
      // every panel).
      Close: {
        extends: 'CloseButton',
        position: 'absolute',
        top: 'Y',
        right: 'Y',
        ariaLabel: (el) => el.call('polyglot', 'workspace.creditBlocker.dismissAria'),
        title: (el) => el.call('polyglot', 'workspace.creditBlocker.dismissTitle'),
        onClick: (e, el, s) => {
          e.stopPropagation()
          el.scope.close(el, s)
        }
      },

      Overline: {
        tag: 'span',
        fontSize: 'Y2',
        fontWeight: '700',
        textTransform: 'uppercase',
        letterSpacing: '.08em',
        color: 'dangerInk',
        text: '{{ workspace.creditBlocker.eyebrow | polyglot }}'
      },

      // The Alarm-archetype numeral — the state IS the number. φ letters are
      // EM-RELATIVE (live-measured here: 'G' ≈ 51px, 'I' ≈ 73px against this
      // card's 17px chain), so a single letter can't reach poster scale.
      // Hero compounds two ladder steps — wrapper 'D' × numeral 'I' ≈ 160px —
      // staying token-pure instead of hardcoding a px size.
      Hero: {
        fontSize: 'D',
        lineHeight: '1',
        Zero: {
          tag: 'span',
          fontFamily: 'Code',
          fontSize: 'I',
          fontWeight: '200',
          lineHeight: '0.9',
          color: 'title',
          ariaHidden: 'true',
          text: '0',
          '@mobileL': { fontSize: 'G' }
        }
      },

      // Mixed-weight identity line: thin context, heavy fact.
      Headline: {
        tag: 'h1',
        margin: '0',
        fontSize: 'C',
        color: 'title',
        lineHeight: '1.15',
        Lead: {
          tag: 'span',
          fontWeight: '300',
          // The light half of the two-weight headline. `{name}` is the
          // workspace's own record name; the bold half (`LeadStrong`) carries
          // the rest of the sentence, so the two keys must be translated as a
          // PAIR — Georgian puts the case marker on this half.
          text: (el, s) =>
            el
              .call('polyglot', 'workspace.creditBlocker.lead')
              .replace(
                '{name}',
                s.root?.workspace?.name ||
                  el.call('polyglot', 'workspace.creditBlocker.thisWorkspace')
              )
        },
        LeadStrong: {
          tag: 'span',
          fontWeight: '700',
          text: '{{ workspace.creditBlocker.headline | polyglot }}'
        }
      },

      Sub: {
        tag: 'p',
        margin: '0',
        fontSize: 'Z2',
        color: 'caption',
        lineHeight: '1.5',
        maxWidth: 'H',
        // Says exactly what is paused and what is not. The old copy promised
        // only metered actions were paused while the gate blocked the whole
        // product — the copy was right and the implementation was wrong, so
        // the implementation moved.
        text: '{{ workspace.creditBlocker.body | polyglot }}'
      },

      // Admins act; members learn who can. Both branches read live role
      // state, so a role change flips the affordance without a reload.
      Cta: {
        hide: (el, s) => !el.call('currentUserIsAdmin', s.root),
        marginTop: 'Z',
        // House primary: inverse fill (title plate, reversed ink) — the
        // DashboardSidebar onboarding CTA pairing. The bare Button base is
        // chromeless, so the fill is declared here.
        Button: {
          text: '{{ workspace.creditBlocker.topUp | polyglot }}',
          minHeight: 'controlH',
          padding: '0 B',
          fontWeight: '700',
          round: 'radiusPill',
          background: 'title',
          color: 'title-reversed',
          cursor: 'pointer',
          border: 'none',
          ':hover': { opacity: '0.85' },
          onClick: (e, el, s) => el.router('/admin/usage', el.getRoot())
        }
      },
      MemberNote: {
        hide: (el, s) => !!el.call('currentUserIsAdmin', s.root),
        tag: 'p',
        margin: '0',
        marginTop: 'Z',
        fontSize: 'Z2',
        color: 'caption',
        text: '{{ workspace.creditBlocker.askAdmin | polyglot }}'
      },

      // The grant context, from the record — never a literal.
      GrantNote: {
        hide: (el, s) => {
          const guard = el.call('creditGuardState', s.root)
          return !(guard && guard.grantSize > 0)
        },
        tag: 'span',
        marginTop: 'A',
        fontSize: 'Y',
        fontFamily: 'Code',
        color: 'caption',
        // One key per form — Georgian keeps the noun singular after a
        // number. The digits keep their source formatting (glossary rule 6).
        text: (el, s) => {
          const guard = el.call('creditGuardState', s.root)
          const n = Number(guard?.grantSize || 0)
          return el
            .call(
              'polyglot',
              n === 1 ? 'workspace.creditBlocker.grantNoteOne' : 'workspace.creditBlocker.grantNote'
            )
            .replace('{count}', n.toLocaleString('en-US'))
        }
      }
    }
  }
}
